import React, { FC, useCallback, useMemo } from 'react';
import classnames from 'classnames';

import classes from './Table.module.scss';
import { Col, Row } from 'antd';
import { ColProps } from 'antd/lib/col';
import { RowProps } from 'antd/lib/row';
import { Icon, IconImage, IconImageType } from 'src/modules/global/components/icon/Icon';
import { rowDefaultProps } from 'src/utils/grid';

export type SortOrderDirection = 'asc' | 'desc';

export interface TableSortOrder {
  name: string;
  direction: SortOrderDirection;
}

export interface TableSortIconProps {
  sortOrder?: TableSortOrder;
  className?: string;
}

export interface TableHeadCellProps extends TableSortIconProps, ColProps {
  name: string;
  className?: string;
  onSort?: (SortOrder) => void;
}

export interface TableContentCellProps extends ColProps {
  className?: string;
  icon?: IconImageType;
}

export interface TableRowProps extends RowProps {}

export const toggleSortOrderDirection = (columnName: string, sortOrder: TableSortOrder) => {
  if (sortOrder.name === columnName) {
    return sortOrder.direction === 'asc' ? 'desc' : 'asc';
  } else {
    return 'asc';
  }
};

export const Table: FC = ({ children }) => {
  return <div className={classes.table}>{children}</div>;
};

export const TableHead: FC = ({ children }) => {
  return (
    <Row {...rowDefaultProps} className={classes.tableHead}>
      {children}
    </Row>
  );
};

const SortIcon: FC<TableSortIconProps> = ({ sortOrder, className }) => {
  const cn = useMemo(() => {
    return classnames(
      classes.sortIcon,
      className,
      sortOrder?.direction === 'asc' ? '' : classes.descendingSortOrder
    );
  }, [className, sortOrder?.direction]);

  if (!sortOrder) {
    return null;
  }

  return <Icon image={IconImage.Sort} className={cn} />;
};

export const TableHeadCell: FC<TableHeadCellProps> = ({
  children,
  name,
  className,
  onSort,
  sortOrder,
  ...rest
}) => {
  const handleClick = useCallback(() => {
    if (onSort) {
      onSort({ name, direction: toggleSortOrderDirection(name, sortOrder) });
    }
  }, [name, onSort, sortOrder]);

  return (
    <Col {...rest} className={classnames(className, classes.tableCell)}>
      <span onClick={handleClick} className={onSort && classes.clickable}>
        {children}
        {
          <SortIcon
            sortOrder={sortOrder}
            className={onSort && sortOrder.name === name && classes.visible}
          />
        }
      </span>
    </Col>
  );
};

export const TableRow: FC<TableRowProps> = ({ children, onClick }) => {
  const cn = useMemo(() => classnames(classes.tableRow, onClick && classes.clickable), [onClick]);
  return (
    <Row {...rowDefaultProps} className={cn} onClick={onClick}>
      {children}
    </Row>
  );
};

export const TableContentCell: FC<TableContentCellProps> = ({
  children,
  className,
  icon,
  ...rest
}) => {
  const cn = useMemo(() => classnames(classes.tableCell, classes.tableContentCell, className), [
    className,
  ]);

  return (
    <Col className={cn} {...rest}>
      {children}
      {icon && <Icon image={icon} size="custom" className={classes.icon} />}
    </Col>
  );
};
